import { useState, type KeyboardEvent } from 'react';
import {
  enabledCountries,
  visibilityText,
  type ConditionalVisibility,
  type FieldDef,
  type FormType,
} from '@jfh/intake-shared';
import { Field, Progress } from '@jfh/intake-forms/renderer';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Chips, StaffLink, StepBar, StepHead } from './parts.js';
import { questionChips, stepsOf, type InsertAt } from './logic.js';
import { useFormPreviewData } from './useFormPreviewData.js';

/** Which page of the canvas is showing: a step, or the staff-only questions. */
export type CanvasView = number | 'staff';

export interface FormCanvasProps {
  fields: FieldDef[];
  formName: string;
  formType: FormType;
  view: CanvasView;
  onView: (v: CanvasView) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Change one question (inline wording and help). */
  onUpdate: (id: string, patch: Partial<FieldDef>) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onReorder: (dragId: string, targetId: string) => void;
  /** Open the add-a-question picker for this spot. */
  onInsert: (at: InsertAt) => void;
  /** Add a new step after the last one. */
  onAddStep: () => void;
  /** Move a whole step to another place (indexes into `stepsOf`). */
  onMoveStep: (from: number, to: number) => void;
}

/** Stops Field's own controls taking focus or clicks: the row is the target. */
const INERT = { inert: '' } as Record<string, string>;

/**
 * The Questions canvas (F9 P3): the form drawn as applicants see it, one step
 * at a time, with the public form's own controls. Click a question to edit it;
 * the selected one's wording and help edit in place. Drag, Alt + arrows or the
 * toolbar reorder; "Add a question here" between questions. The step bar
 * picks, reorders and adds steps.
 */
export function FormCanvas(props: FormCanvasProps) {
  const { fields, formType, view, onView, selectedId, onSelect, onUpdate, onInsert } = props;
  const toast = useToast();
  const { resolved, countryOptions, countryConfig } = useFormPreviewData(fields);
  const hasRules = (f: FieldDef | null) => Boolean((f?.visibleWhen as ConditionalVisibility | undefined)?.rules?.length);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const steps = stepsOf(resolved);
  const staffView = view === 'staff';
  const si = staffView ? -1 : Math.min(view, steps.length - 1);
  const step = staffView ? null : steps[si]!;
  const staff = resolved.filter((f) => f.audience === 'staff');
  const items = staffView ? staff : step!.items;
  const head = step?.head ?? null;
  const headSelected = Boolean(head && head.id === selectedId);
  const lastStep = !staffView && si === steps.length - 1;

  const countries = countryOptions.length
    ? countryOptions.map((o) => ({ iso2: o.value, name: o.label, dial: '' }))
    : enabledCountries(countryConfig.disabled);

  return (
    <div className="scroll-y" style={{ background: 'var(--jfh-sea-shell)' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '18px 40px 80px' }}>
        <div className="bld-panel">
          <div style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="drag" size={14} /> This is the form applicants see. Click a question to change it, drag to
            reorder (or Alt + ← / →).
          </div>

          <StepBar
            steps={steps}
            current={staffView ? null : si}
            onPick={(i) => {
              // Picking the step that's showing opens its settings.
              const h = steps[i]?.head;
              if (i === si && h) onSelect(h.id);
              else onView(i);
            }}
            onMove={props.onMoveStep}
            onAdd={props.onAddStep}
          />

          <div className="bld-step-frame">
            {step && (
              <StepHead
                n={si + 1}
                total={steps.length}
                name={head?.label || 'Your details'}
                count={step.items.length}
                rule={hasRules(head) ? visibilityText(head!.visibleWhen as ConditionalVisibility, resolved) : null}
                onEdit={head ? () => onSelect(head.id) : undefined}
              />
            )}
            <div
              className="bld-step-body"
              style={staffView ? { borderTop: '4px solid var(--jfh-cactus)' } : undefined}
            >
              {staffView ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--jfh-cactus)' }}>
                    <Icon name="shield" size={15} />
                    <span className="mono rdr-step-label">Staff only</span>
                  </div>
                  <h2 className="rdr-step-title">Filled in by JFH staff</h2>
                  <p className="rdr-step-sub">
                    Applicants never see these. They appear on the Teams card and in the portal when someone approves the
                    application.
                  </p>
                </>
              ) : (
                <>
                  <Progress
                    step={si}
                    total={steps.length + 1}
                    labels={[...steps.map((st) => st.head?.label || 'Your details'), 'Check and send']}
                  />
                  <div className="rdr-step-label">
                    Step {si + 1} of {steps.length + 1}
                  </div>
                  {headSelected && head ? (
                    <>
                      <input
                        className="rdr-step-title bld-inline"
                        value={head.label}
                        onChange={(e) => onUpdate(head.id, { label: e.target.value })}
                        aria-label="Step title"
                        autoFocus
                      />
                      <div className="bld-step-bar" aria-hidden="true" />
                    </>
                  ) : (
                    <h2
                      className="rdr-step-title"
                      onClick={() => head && onSelect(head.id)}
                      title={head ? 'Click to rename this step' : undefined}
                      style={{ cursor: head ? 'pointer' : 'default' }}
                    >
                      {head?.label || 'Your details'}
                    </h2>
                  )}
                  {head?.help ? (
                    <p className="rdr-step-sub">{head.help}</p>
                  ) : (
                    head && (
                      <p className="rdr-step-sub" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
                        No line under the title. Click the title to add one.
                      </p>
                    )
                  )}
                </>
              )}

              <div className="rdr-fields" style={{ gap: 4 }}>
                {items.length === 0 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '28px 12px',
                      color: 'var(--text-3)',
                      fontSize: 13,
                      border: '1.5px dashed var(--border-strong)',
                      borderRadius: 'var(--r-lg)',
                    }}
                  >
                    {staffView
                      ? 'No staff-only questions yet. Add one for anything staff fill in while approving, like an account code or credit limit.'
                      : 'This step has no questions yet. Add the first one below.'}
                  </div>
                )}
                {items.map((f) => (
                  <QuestionRow
                    key={f.id}
                    field={f}
                    fields={resolved}
                    formType={formType}
                    selected={f.id === selectedId}
                    dragging={dragId === f.id}
                    dropTarget={overId === f.id && dragId !== null && dragId !== f.id}
                    countries={countries}
                    defaultCountryIso={countryConfig.default}
                    props={props}
                    onDragStart={() => setDragId(f.id)}
                    onDragOver={() => setOverId(f.id)}
                    onDrop={() => {
                      if (dragId) props.onReorder(dragId, f.id);
                      setDragId(null);
                      setOverId(null);
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverId(null);
                    }}
                  />
                ))}
                <button
                  type="button"
                  className="bld-add-end"
                  onClick={() => onInsert(staffView ? { staff: true } : { step: si })}
                >
                  <Icon name="plus" size={15} /> {staffView ? 'Add a staff-only question' : 'Add a question to this step'}
                </button>
              </div>

              {!staffView && (
                <div className="rdr-nav">
                  {si > 0 && (
                    <button type="button" className="btn lg" onClick={() => onView(si - 1)}>
                      <Icon name="arrowLeft" size={16} /> Back
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn primary lg rdr-grow"
                    onClick={() =>
                      lastStep
                        ? toast({
                            title: 'Applicants check their answers next',
                            body: 'The review page lists every answer with an Edit link. Try it out to see it.',
                            icon: 'forms',
                          })
                        : onView(si + 1)
                    }
                  >
                    {lastStep ? 'Check your answers' : 'Continue'} <Icon name="chevronR" size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <StaffLink
          staffView={staffView}
          count={staff.length}
          steps={steps.length}
          onClick={() => onView(staffView ? 0 : 'staff')}
        />
      </div>
    </div>
  );
}

interface QuestionRowProps {
  field: FieldDef;
  fields: FieldDef[];
  formType: FormType;
  selected: boolean;
  dragging: boolean;
  dropTarget: boolean;
  countries: ReturnType<typeof enabledCountries>;
  defaultCountryIso: string;
  props: FormCanvasProps;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}

function QuestionRow({
  field: f,
  fields,
  formType,
  selected,
  dragging,
  dropTarget,
  countries,
  defaultCountryIso,
  props,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: QuestionRowProps) {
  const { onSelect, onUpdate, onMove, onDuplicate, onDelete, onInsert } = props;
  const chips = questionChips(f, fields, formType);
  const showLabel = f.type !== 'consent' && f.type !== 'hidden';

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.altKey && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      onMove(f.id, e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1);
      onSelect(f.id);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(f.id);
    }
  };
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div>
      <div className="bld-insert">
        <button type="button" onClick={stop(() => onInsert({ beforeId: f.id }))}>
          <Icon name="plus" size={12} /> Add a question here
        </button>
      </div>
      <div
        className={`bld-q${selected ? ' selected' : ''}`}
        data-field-id={f.id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          onDragStart();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver();
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDrop();
        }}
        onDragEnd={onDragEnd}
        onClick={() => onSelect(f.id)}
        onKeyDown={onKey}
        tabIndex={0}
        role="button"
        aria-label={`Edit question: ${f.label}`}
        aria-pressed={selected}
        style={{
          opacity: dragging ? 0.4 : 1,
          outline: dropTarget ? '2px dashed var(--accent)' : undefined,
        }}
      >
        <span className="bld-q-grip" title="Drag to reorder">
          <Icon name="drag" size={14} />
        </span>
        {selected && (
          <div className="bld-q-toolbar">
            <button type="button" className="icon-btn" onClick={stop(() => onMove(f.id, -1))} title="Move up (Alt + ↑)" aria-label="Move up">
              ↑
            </button>
            <button type="button" className="icon-btn" onClick={stop(() => onMove(f.id, 1))} title="Move down (Alt + ↓)" aria-label="Move down">
              ↓
            </button>
            <button type="button" className="icon-btn" onClick={stop(() => onDuplicate(f.id))} title="Duplicate" aria-label="Duplicate">
              <Icon name="copy" size={13} />
            </button>
            <button
              type="button"
              className="icon-btn"
              style={{ color: 'var(--st-declined)' }}
              onClick={stop(() => onDelete(f.id))}
              title="Delete"
              aria-label="Delete"
            >
              <Icon name="trash" size={13} />
            </button>
          </div>
        )}

        {showLabel && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
            {selected ? (
              <input
                className="rdr-field-label bld-inline"
                value={f.label}
                onChange={(e) => onUpdate(f.id, { label: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                aria-label="Question wording"
              />
            ) : (
              <span className="rdr-field-label">{f.label || 'Untitled question'}</span>
            )}
            {f.required && (
              <span className="rdr-req" title="Required">
                *
              </span>
            )}
          </div>
        )}

        <Chips chips={chips} />

        {f.type === 'hidden' ? (
          <div className="bld-hidden-value">
            <Icon name="eye-off" size={14} /> Hidden value: <strong>{f.label}</strong> ={' '}
            <span className="mono">{f.defaultValue || '(empty)'}</span>
          </div>
        ) : (
          <div className="bld-preview" {...INERT}>
            <Field
              field={f}
              value={undefined}
              onChange={() => undefined}
              countries={countries}
              defaultCountryIso={defaultCountryIso}
            />
          </div>
        )}

        {f.type !== 'hidden' &&
          (selected ? (
            <input
              className="field-help bld-inline"
              value={f.help ?? ''}
              onChange={(e) => onUpdate(f.id, { help: e.target.value || undefined })}
              onClick={(e) => e.stopPropagation()}
              placeholder="Add help text (optional)"
              aria-label="Help text"
            />
          ) : (
            f.help && f.type !== 'consent' && <div className="field-help">{f.help}</div>
          ))}
      </div>
    </div>
  );
}
